import React, { useEffect, useMemo, useState } from "react";
import { ModalVertFixed } from "src/components/ModalVertFixed";
import { Flex, notification, Spin, Tree } from "antd";
import { observer } from "mobx-react-lite";
import { t } from "i18next";
import { TableStore } from "src/components/tables/TableStore";
import { onError } from "src/common/onError";
import { CopyOutlined } from "@ant-design/icons";
import { DropdownChildButton } from "src/components/buttons/DropdownChildButton";
import { ZUid } from "src/types/zUid";
import { useNavigate } from "react-router-dom";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";
import { EntityCopyButtonStore } from "./EntityCopyButtonStore";
import styles from "./EntityCopyButton.module.less";
import { ZEntityRow, EntityFilters } from "../types";
import { EntCopyTreeNode } from "./types";

interface PropsEntityCopyButton {
  tableStore: TableStore<ZEntityRow, EntityFilters>;
  objectId: ZUid;
  onLoad?: (flag: boolean) => void;
}

export const EntityCopyButton: React.FC<PropsEntityCopyButton> = observer(
  ({ tableStore, objectId, onLoad }) => {
    const navigate = useNavigate();
    const store = useMemo(
      () => new EntityCopyButtonStore(objectId, tableStore),
      [],
    );
    const [modalOpen, setModalOpen] = useState(false);
    const [wait, setWait] = useState(false);

    const {
      defaultChecked,
      checkedKeys,
      onCheck,
      treeData,
      expandedKeys,
      defaultExpanded,
      onExpand,
    } = store;

    const handleCopy = () => {
      setWait(true);
      store
        .copyEntityWithBSs()
        .then((id) => {
          notification.success({ message: "Entity copied successfully" });
          navigate(makeUrl(PageUrl.entityCard, { id }));
        })
        .finally(() => {
          setModalOpen(false);
          setWait(false);
        })
        .catch(onError);
    };

    useEffect(() => {
      if (modalOpen) {
        store.setCheckedItems(defaultChecked ?? []);
        store.setExpanded(defaultExpanded ?? []);
      }
    }, [modalOpen]);

    const onClick = () => {
      onLoad?.(true);
      store
        .init()
        .then((res) => {
          if (res === null) return;
          if (res.childAttributes?.length) {
            setModalOpen(true);
          } else {
            handleCopy();
          }
        })
        .finally(() => {
          onLoad?.(false);
        })
        .catch(onError);
    };

    return (
      <>
        <DropdownChildButton
          onClick={onClick}
          icon={<CopyOutlined />}
          disabled={tableStore.selected.length !== 1}
        >
          {t("Copying an instance")}
        </DropdownChildButton>
        <ModalVertFixed
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          okButtonProps={{ disabled: wait }}
          onOk={handleCopy}
          okText={t("Copy")}
          title={t("Copying an instance")}
        >
          <Spin spinning={wait}>
            <Flex vertical gap={8}>
              <div className={styles.subTitle}>
                {t("Select dependent objects to copy")}
              </div>
              <Tree<EntCopyTreeNode>
                treeData={treeData}
                checkable
                expandedKeys={expandedKeys}
                onExpand={onExpand}
                selectable={false}
                checkedKeys={checkedKeys}
                checkStrictly
                onCheck={onCheck}
              />
            </Flex>
          </Spin>
        </ModalVertFixed>
      </>
    );
  },
);
